// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    FC,
    useEffect,
    useState,
} from 'react';
import {
    DiskCacheMode,
    DiskDriver,
    IKVMVirtualServerSettingsRequest,
    IVmResponse,
    OsFirmware,
    VirtualServerSettingsRequest,
} from 'common/api/resources/ComputeResourceVm';
import {
    Alert,
    Grid,
    GridCol,
    Icon,
    Item,
    Paragraph,
    Status,
    Translate,
} from '@plesk/ui-library';
import ButtonWithConfirmation from 'common/components/ButtonWithConfirmation';
import {
    ICONS,
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import {
    Column,
    ItemText,
} from 'common/components/ServerTabs/SettingsTab/Styles';
import SuspendResumeControl from 'common/components/ServerTabs/components/Controls/SuspendResumeControl';
import { VerticalIndent } from 'common/components/styles/VerticalIndent';
import SelectWithConfirmation from 'common/components/SelectWithConfirmation/SelectWithConfirmation';
import { ISelectOption } from 'common/components/SelectInput';
import { PropsValue } from 'react-select';
import { SwitchWithConfirmation } from 'common/components/SwitchWithConfirmation';
import { connect } from 'react-redux';
import { ICommonState } from 'common/store';
import { isFeatureFlagEnabled } from 'common/modules/app/features/utils';
import { FEATURES } from 'common/modules/app/features/constants';
import { RootState as AdminRootState } from 'admin/core/store';

export interface IKVMSettingsTabProps {
    server: IVmResponse;
    isUpdating: boolean;
    updateSettings: (settings: VirtualServerSettingsRequest) => void;
    isInstallingGuestTools: boolean;
    installGuestTools: () => void;
    isSuspendToggling: boolean;
    toggleSuspendStatus: (isSuspended: boolean) => void;
    canManageServers: boolean;
    isHighAvailabilitySettingsUpdating: boolean;
    updateHighAvailabilityEnabled: (id: number, isEnabled: boolean) => void;
}

export type KVMSettingsTabProps =
    IKVMSettingsTabProps &
    ReturnType<typeof mapStateToProps>;

const diskCacheModeOptions: ISelectOption[] = Object.values(DiskCacheMode).map(mode => ({
    value: mode,
    label: mode,
}));

const getDiskCacheModeValue = (value?: DiskCacheMode) =>
    diskCacheModeOptions.find((option) => option.value === value);

const diskDriverOptions: ISelectOption[] = Object.values(DiskDriver).map(mode => ({
    value: mode,
    label: mode,
}));

const getDiskDriverValue = (value?: DiskDriver) =>
    diskDriverOptions.find((option) => option.value === value);

const firmwareOptions: ISelectOption[] = Object.values(OsFirmware).map(mode => ({
    value: mode,
    label: mode,
}));

const getFirmwareValue = (value?: OsFirmware) =>
    firmwareOptions.find((option) => option.value === value);

export const KVMSettingsTab: FC<KVMSettingsTabProps> = ({
    server,
    isUpdating,
    updateSettings,
    isInstallingGuestTools,
    installGuestTools,
    isSuspendToggling,
    toggleSuspendStatus,
    canManageServers,
    isHighAvailabilitySettingsUpdating,
    updateHighAvailabilityEnabled,
    isHighAvailabilityCrFeatureFlagEnabled,
}) => {
    const [loadingProperty, setLoadingProperty] =
        useState<keyof IKVMVirtualServerSettingsRequest>();

    const diskCacheModeChange = (option: PropsValue<ISelectOption>) => {
        setLoadingProperty('disk_cache_mode');
        updateSettings({ disk_cache_mode: (option as ISelectOption).value as DiskCacheMode });
    };

    const diskDriverChange = (option: PropsValue<ISelectOption>) => {
        setLoadingProperty('disk_driver');
        updateSettings({ disk_driver: (option as ISelectOption).value as DiskDriver });
    };

    const firmwareChange = (option: PropsValue<ISelectOption>) => {
        setLoadingProperty('firmware');
        updateSettings({ firmware: (option as ISelectOption).value as OsFirmware });
    };

    useEffect(() => {
        if (!isUpdating) {
            setLoadingProperty(undefined);
        }
    }, [isUpdating]);

    return (
        <Grid gap="md">
            <GridCol xs={12} sm={12} md={12} xl={5} lg={5}>
                <Column>
                    <Item
                        title={
                            <h3><Translate content="servers.tabs.settings.general.title" /></h3>
                        }
                        view="card"
                    >
                        <div>
                            <div id="disk-cache-mode-label">
                                <Translate content="servers.tabs.settings.diskCacheMode.title" />
                            </div>
                            <SelectWithConfirmation
                                inputId="disk-cache-mode"
                                aria-labelledby="disk-cache-mode-label"
                                minHeight="40px"
                                width="350px"
                                onChange={diskCacheModeChange}
                                value={getDiskCacheModeValue(server.settings.disk_cache_mode)}
                                options={diskCacheModeOptions}
                                isDisabled={isUpdating}
                                isLoading={isUpdating && loadingProperty === 'disk_cache_mode'}
                                dialog={{
                                    title: <Translate content="servers.tabs.settings.diskCacheMode.confirmation.title" />,
                                    text: <Translate content="servers.tabs.settings.diskCacheMode.confirmation.text" />,
                                    button: <Translate content="servers.tabs.settings.diskCacheMode.confirmation.button" />,
                                }}
                            />
                        </div>
                        <VerticalIndent />
                        <div>
                            <div id="disk-driver-label">
                                <Translate content="servers.tabs.settings.diskDriver.title" />
                            </div>
                            <SelectWithConfirmation
                                inputId="disk-driver"
                                aria-labelledby="disk-driver-label"
                                minHeight="40px"
                                width="350px"
                                onChange={diskDriverChange}
                                value={getDiskDriverValue(server.settings.disk_driver)}
                                options={diskDriverOptions}
                                isDisabled={isUpdating}
                                isLoading={isUpdating && loadingProperty === 'disk_driver'}
                                dialog={{
                                    title: <Translate content="servers.tabs.settings.diskDriver.confirmation.title" />,
                                    text: <Translate content="servers.tabs.settings.diskDriver.confirmation.text" />,
                                    button: <Translate content="servers.tabs.settings.diskDriver.confirmation.button" />,
                                }}
                            />
                            {server.settings.disk_driver === DiskDriver.VIRTIO &&
                                server.plan.is_thin_provisioned &&
                                server.compute_resource?.capabilities.is_virtio_discard_supported === false &&
                                <Alert intent={INTENT_TYPE.WARNING}>
                                    <Icon intent={INTENT_TYPE.WARNING} name={ICONS.TRIANGLE_EXCLAMATION_MARK_FILLED} />
                                    &nbsp;
                                    <Translate content="servers.tabs.settings.diskDriver.warning" />
                                </Alert>
                            }
                        </div>
                        <VerticalIndent />
                        <div>
                            <div id="firmware-label">
                                <Translate content="servers.tabs.settings.firmware.title" />
                            </div>
                            <SelectWithConfirmation
                                inputId="firmware"
                                aria-labelledby="firmware-label"
                                minHeight="40px"
                                width="350px"
                                onChange={firmwareChange}
                                value={getFirmwareValue(server.settings.firmware ? server.settings.firmware : OsFirmware.BIOS)}
                                options={firmwareOptions}
                                isDisabled={isUpdating}
                                isLoading={isUpdating && loadingProperty === 'firmware'}
                                dialog={{
                                    title: <Translate content="servers.tabs.settings.firmware.confirmation.title" />,
                                    text: <Translate content="servers.tabs.settings.firmware.confirmation.text" />,
                                    button: <Translate content="servers.tabs.settings.firmware.confirmation.button" />,
                                }}
                            />
                        </div>
                        <VerticalIndent />
                        <SuspendResumeControl
                            value={server.is_suspended}
                            disabled={isSuspendToggling || !canManageServers}
                            loading={isSuspendToggling}
                            onChange={toggleSuspendStatus}
                        />
                        <Paragraph>
                            <Translate content="servers.tabs.settings.suspendResume.text" />
                        </Paragraph>
                    </Item>
                </Column>
            </GridCol>

            <GridCol xs={12} sm={12} md={12} xl={4} lg={4}>
                <Column>
                    <Item
                        title={
                            <h3><Translate content="servers.tabs.settings.guestTools.title" /></h3>
                        }
                        view="card"
                    >
                        <Paragraph>
                            <Translate content="servers.tabs.settings.guestTools.description" />
                        </Paragraph>
                        <VerticalIndent />
                        <Paragraph>
                            <Status
                                intent={server.settings.guest_tools_installed ? INTENT_TYPE.SUCCESS : INTENT_TYPE.INACTIVE}
                                compact={true}
                            >
                                {server.settings.guest_tools_installed
                                    ? <Translate content="servers.tabs.settings.guestTools.installed" />
                                    : <Translate content="servers.tabs.settings.guestTools.notInstalled" />}
                            </Status>
                        </Paragraph>
                        <Paragraph>
                            <Status
                                intent={server.settings.guest_agent_available ? INTENT_TYPE.SUCCESS : INTENT_TYPE.INACTIVE}
                                compact={true}
                            >
                                {server.settings.guest_agent_available
                                    ? <Translate content="servers.tabs.settings.guestTools.agentAvailable" />
                                    : <Translate content="servers.tabs.settings.guestTools.agentNotAvailable" />}
                            </Status>
                        </Paragraph>
                        <VerticalIndent />
                        <ButtonWithConfirmation
                            isLoading={isInstallingGuestTools}
                            disabled={isInstallingGuestTools}
                            buttonSize={SIZE.LG}
                            buttonIntent={INTENT_TYPE.PRIMARY}
                            confirmationButtonGhost={false}
                            translations={{
                                title: <Translate content="servers.tabs.settings.guestTools.confirmationDialog.title" />,
                                text: <Translate content="servers.tabs.settings.guestTools.confirmationDialog.content" />,
                                button: <Translate content="servers.tabs.settings.guestTools.confirmationDialog.button" />,
                            }}
                            confirmationButtonText={
                                <Translate content='servers.tabs.settings.guestTools.button' />
                            }
                            handleConfirm={installGuestTools}
                        />
                    </Item>

                    {isHighAvailabilityCrFeatureFlagEnabled && (
                        <Item
                            title={
                                <h3><Translate content="highAvailabilityCard.title" /></h3>
                            }
                            view="card"
                        >
                            <SwitchWithConfirmation
                                checked={server.is_high_availability_cr_enabled}
                                disabled={!server.plan.is_high_availability_cr_enabled || isHighAvailabilitySettingsUpdating}
                                loading={isHighAvailabilitySettingsUpdating}
                                onChange={(isEnabled: boolean) => updateHighAvailabilityEnabled(server.id, isEnabled)}
                                tooltip={!server.plan.is_high_availability_cr_enabled
                                    ? <Translate content="highAvailabilityCard.switchTooltip.planNotAvailable" />
                                    : undefined
                                }
                                confirmOnEnable={{
                                    enabled: server.plan.high_availability_cr_price > 0,
                                    translations: {
                                        title: <Translate content="highAvailabilityCard.enableConfirm.title" />,
                                        text: <Translate content="highAvailabilityCard.enableConfirm.text" params={{ percent: server.plan.high_availability_cr_price }} />,
                                        button: <Translate content="highAvailabilityCard.enableConfirm.button" />,
                                    },
                                }}
                                confirmOnDisable={{
                                    enabled: true,
                                    translations: {
                                        title: <Translate content="highAvailabilityCard.disableConfirm.title" />,
                                        text: <Translate content="highAvailabilityCard.disableConfirm.text" />,
                                        button: <Translate content="highAvailabilityCard.disableConfirm.button" />,
                                    },
                                }}
                            />
                            <ItemText>
                                <Translate content="highAvailabilityCard.content.text" />
                            </ItemText>
                        </Item>
                    )}
                </Column>
            </GridCol>
        </Grid>
    );
};

const mapStateToProps = (state: ICommonState) => ({
    isHighAvailabilityCrFeatureFlagEnabled: isFeatureFlagEnabled(state as unknown as AdminRootState, FEATURES.HIGH_AVAILABILITY_CR),
});

export default connect(mapStateToProps)(KVMSettingsTab);
